import React, { useContext, useEffect, useRef, useState } from "react";
import { useNotifContext } from "../../../components/gcadmin/admintopbar/AdminTopbar";
import "../../../assets/css/gcadmin/adminnotifications/AdminNotifications.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

const ITEMS_PER_PAGE = 10; // yahan se page size control hoti hai

/* ─── Notification Icon ─── */
const NotifIcon = ({ type, size = "md" }) => {
  const colorMap = {
    overdue: "gcAdminNP-icon--danger",
    fine:    "gcAdminNP-icon--danger",
    book:    "gcAdminNP-icon--success",
    review:  "gcAdminNP-icon--info",
    dispute: "gcAdminNP-icon--warning",
  };
  const symbols = { overdue: "!", fine: "₨", book: "✦", review: "★", dispute: "⚠" };
  return (
    <span
      className={`gcAdminNP-icon gcAdminNP-icon--${size} ${colorMap[type] || colorMap.review}`}
      aria-hidden="true"
    >
      {symbols[type] || "●"}
    </span>
  );
};

/* ─── Main AdminNotificationsPage ─── */
const AdminNotificationsPage = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);
  const ctx = useNotifContext();
  const [localNotifications, setLocalNotifications] = useState([]);

  const notifications = ctx ? ctx.notifications : localNotifications;
  const setNotifications = ctx ? ctx.setNotifications : setLocalNotifications;

  const [loading, setLoading] = useState(false);       // initial API fetch loading
  const [currentPage, setCurrentPage] = useState(1);    // pagination state

  // Pagination change hone par sirf current page (max 10) notifications
  // ki chhoti loading dikhane ke liye — poori list dobara load nahi hoti
  const [viewLoading, setViewLoading] = useState(false);
  const isFirstRender = useRef(true);

  const unreadCount = notifications?.filter((n) => n.status === "unread").length;

  const selectNotif = (n) => {
    setNotifications((prev) =>
      prev.map((item) => (item.id === n.id ? { ...item, status: "read" } : item)),
    );
  };

  /* ── Paginated slice (jo list ko jayega) ── */
  const paginatedNotifications = (notifications || []).slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Fetch all notifications ── */
  const fetchAllNotifications = () => {
    if (!loggedInUser?.id) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("lbadmin_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/admin/notification/fetchAllNotifications`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setLocalNotifications(res.data.notifications);
        } else {
          setLocalNotifications([]);
        }
        setCurrentPage(1); // fresh fetch pe page 1 pe reset
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  /* ── Mark all as read ── */
  const markAllAsRead = () => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("lbadmin_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/admin/notification/markAllAsRead`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          Swal.fire({
            title: 'Wow',
            icon: 'success',
            text: 'All Notifcations Marked As Read',
            confirmButtonText: 'Okay'
          });
          fetchAllNotifications();
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /* ── Clear all notifications ── */
  const clearAllNotifications = () => {
    if (!loggedInUser?.id) return;

    Swal.fire({
      title: 'Clear All Notifications?',
      text: 'This will permanently delete all notifications.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear all',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("lbadmin_id", loggedInUser.id);

        axios
          .post(`${baseUrl}/admin/notification/deleteAllNotifications`, formData, {
            headers: {
              "Content-Type": "multipart/formData",
              "Authorization": `Bearer ${authToken}`,
            },
          })
          .then((res) => {
            if (res.data.success === true) {
              setLocalNotifications([]);
              setCurrentPage(1);
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      fetchAllNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* ── Sirf current page ke (max 10) notifications ki chhoti (400ms)
     loading dikhayein — jab pagination se dusre page par jayen.
     Pehli render par yeh nahi chalta (initial fetch ka `loading` state
     us waqt already handle kar raha hota hai). ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [currentPage]);

  // initial fetch loading + pagination ki chhoti loading dono combine
  const showLoader = loading || viewLoading;

  return (
    <div className="gcAdminNP-root">
      {/* ── Page Header ── */}
      <div className="gcAdminNP-page-head">
        <div>
          <h1 className="gcAdminNP-page-title">Notifications</h1>
          <p className="gcAdminNP-page-sub">
            Stay updated with your library activity and alerts.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {notifications.length > 0 && (
            <button className="gcAdminNP-markall-btn" onClick={markAllAsRead}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7L5.5 10.5L12 3.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Mark All as Read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              className="gcAdminNP-markall-btn"
              style={{ color: "#d33", borderColor: "#d33" }}
              onClick={clearAllNotifications}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 3.5H12M5 3.5V2.5H9V3.5M4 3.5V11.5H10V3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* ── Layout ── */}
      <div className="gcAdminNP-layout">
        {/* ── List ── */}
        <div className="gcAdminNP-list-col">
          <div className="gcAdminNP-section-head">
            <span className="gcAdminNP-section-head__dot" aria-hidden="true" />
            <h2 className="gcAdminNP-section-head__title">
              Recent Notifications
            </h2>
            {unreadCount > 0 && (
              <span className="gcAdminNP-section-head__badge">
                {unreadCount}
              </span>
            )}
          </div>

          {showLoader ? (
            <Loader message="Notifications loading, please wait..." />
          ) : (
            <>
              <ul className="gcAdminNP-list" role="list">
                {paginatedNotifications.map((n, idx) => (
                  <li
                    key={n.id}
                    className={[
                      "gcAdminNP-row",
                      n.status === "unread" ? "gcAdminNP-row--unread" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ animationDelay: `${idx * 0.055}s` }}
                    onClick={() => selectNotif(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && selectNotif(n)}
                  >
                    <NotifIcon type={n.type} />
                    <div className="gcAdminNP-row__body">
                      <div className="gcAdminNP-row__top">
                        <span className="gcAdminNP-row__title">
                          {n.title}
                          {n.status === "unread" && (
                            <span
                              className="gcAdminNP-row__dot"
                              aria-label="unread"
                            />
                          )}
                        </span>
                      </div>
                      <p className="gcAdminNP-row__summary">{n.subtitle}</p>
                    </div>
                    {n.status === "read" && (
                      <span className="gcAdminNP-row__read-tick" aria-label="Read">
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <path
                            d="M2 6.5L5 9.5L11 3.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}

                {notifications.length === 0 && (
                  <li className="gcAdminNP-empty">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <path
                        d="M20 5a11 11 0 0111 11v6l3 4H6l3-4v-6A11 11 0 0120 5zM17 35a3 3 0 006 0"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span>No notifications yet</span>
                  </li>
                )}
              </ul>

              <Pagination
                currentPage={currentPage}
                totalItems={notifications.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationsPage;